'use no memo';

import { type Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Form,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@kalos/ui';
import { CheckCircledIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { type MemoFilter, useMemoQuery } from '../../../../hooks/react-query/useMemoQuery';
import { type Column, ExportJSON } from '../../ExportJSON';
import { MemoExportFilter } from './MemoExportFilter';
import {
  getDefaultMemoExportFields,
  type MemoExportLockedFields,
  type MemoExportSchemeType,
  useMemoExportForm,
} from './MemoExportUtils';

interface MemoExportBaseProps {
  /** Optional: lock certain fields in the form (show but disable) */
  lockedFields?: MemoExportLockedFields;
}

interface MemoExportWithFormProps extends MemoExportBaseProps {
  /** Show the filter form for user input */
  showFilterForm: true;
  /** Initial values for the filter form */
  initialFormValues?: Partial<MemoExportSchemeType>;
  /** Direct filter - not used when showFilterForm is true */
  filter?: never;
}

interface MemoExportWithFilterProps extends MemoExportBaseProps {
  /** Don't show the filter form */
  showFilterForm?: false;
  /** Direct filter to use for export (no form shown) */
  filter: Partial<MemoFilter>;
  /** Initial form values - not used when showFilterForm is false */
  initialFormValues?: never;
}

export type MemoExportProps = MemoExportWithFormProps | MemoExportWithFilterProps;

const convertExportFormValuesToMemoFilters = (filter: MemoExportSchemeType): MemoFilter => {
  const baseFilter: MemoFilter = {
    isActive: true,
    includeVideoFiles: true,
    includeFiles: true,
    includeUnsupportedMobileFiles: true,
    orderBy: 'id',
    orderDir: 'DESC',
    includeCommentCount: false,
    eventId: filter.eventId,
    withoutLimit: true,
  };

  if (filter.startDate && filter.endDate) {
    baseFilter.dateRange = ['>=', filter.startDate, '<', filter.endDate];
  } else if (filter.startDate) {
    baseFilter.dateRange = ['>=', filter.startDate];
  } else if (filter.endDate) {
    baseFilter.dateRange = ['<', filter.endDate];
  }

  return baseFilter as MemoFilter;
};

const exportColumns: Column[] = [
  { label: 'ID', value: 'id' },
  { label: 'Name', value: 'name' },
  { label: 'Description', value: 'description' },
  { label: 'Transcript', value: 'transcript' },
  { label: 'Summary', value: 'summary' },
  { label: 'Created By', value: 'createdByName' },
  { label: 'Department', value: 'userDepartmentName' },
  { label: 'Job Number', value: 'eventId' },
  { label: 'Property ID', value: 'propertyId' },
  { label: 'Date Created', value: 'createdDate' },
];

const formatMemoForExport = (memo: Memo) => {
  // Combine transcripts and summaries from all files
  const files = memo.files?.results || [];
  const transcripts = files
    .map((file) => file.fileTranscript)
    .filter(Boolean)
    .join('\n---\n');
  const summaries = files
    .map((file) => file.fileSummary)
    .filter(Boolean)
    .join('\n---\n');

  return {
    id: memo.id,
    name: memo.name || '',
    description: memo.description || '',
    transcript: transcripts,
    summary: summaries,
    createdByName: memo.createdByName || '',
    userDepartmentName: memo.userDepartmentName || '',
    eventId: memo.eventId || '',
    propertyId: memo.propertyId || '',
    createdDate: memo.createdDate || '',
  };
};

export const MemoExport = (props: MemoExportProps) => {
  const { lockedFields } = props;
  const showFilterForm = props.showFilterForm ?? false;

  const [shouldFetch, setShouldFetch] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<MemoFilter | null>(null);
  const [exportComplete, setExportComplete] = useState(false);

  // Form is only used when showFilterForm is true
  const form = useMemoExportForm({
    defaultValues: useMemo(
      () =>
        getDefaultMemoExportFields(
          showFilterForm ? (props as MemoExportWithFormProps).initialFormValues : undefined,
        ),
      [showFilterForm, props],
    ),
  });

  const memoQuery = useMemoQuery({
    filters: currentFilter || {},
    enabled: shouldFetch && currentFilter !== null,
  });

  const exportData = useMemo(() => {
    if (!memoQuery.data?.results) return [];
    return memoQuery.data.results.map(formatMemoForExport);
  }, [memoQuery.data?.results]);

  const handleSearchClick = useCallback(() => {
    let filterToUse: MemoFilter;

    if (showFilterForm) {
      // Build filter from form values
      const formValues = form.getValues();
      filterToUse = convertExportFormValuesToMemoFilters(formValues);
    } else {
      // Use direct filter, ensure withoutLimit and includeFiles are set
      const directFilter = (props as MemoExportWithFilterProps).filter;
      filterToUse = {
        isActive: true,
        includeVideoFiles: true,
        includeFiles: true,
        includeUnsupportedMobileFiles: true,
        includeCommentCount: false,
        orderBy: 'id',
        orderDir: 'DESC',
        withoutLimit: true,
        ...directFilter,
      } as MemoFilter;
    }

    setCurrentFilter(filterToUse);
    setShouldFetch(true);
    setExportComplete(false);
  }, [form, showFilterForm, props]);

  const handleExported = useCallback(() => {
    setExportComplete(true);
  }, []);

  const getFilename = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const eventId = showFilterForm
      ? form.getValues().eventId
      : (props as MemoExportWithFilterProps).filter?.eventId;
    if (eventId) {
      return `notes-export-job-${eventId}-${timestamp}`;
    }
    return `notes-export-${timestamp}`;
  };

  // Determine if we have results ready for export
  const hasResultsReady = memoQuery.isSuccess && exportData.length > 0;

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Filter Section - only shown when showFilterForm is true */}
      {showFilterForm && (
        <div className="rounded-lg border bg-white p-4">
          <h3 className="mb-4 text-sm font-medium text-gray-700">Export Filters</h3>
          <Form {...form}>
            <MemoExportFilter lockedFields={lockedFields} disabled={memoQuery.isLoading} />
          </Form>
        </div>
      )}

      {/* Status Indicator */}
      {shouldFetch && (
        <div className="flex items-center gap-2 rounded-lg border bg-white p-4">
          {memoQuery.isLoading ? (
            <>
              <Loader2 className="size-5 animate-spin text-blue-500" />
              <span className="text-sm text-gray-600">Loading notes...</span>
            </>
          ) : exportComplete ? (
            <>
              <CheckCircledIcon className="size-5 text-green-500" />
              <span className="text-sm text-green-600">
                Export complete! {memoQuery.data?.totalCount || 0} notes exported.
              </span>
            </>
          ) : memoQuery.isSuccess ? (
            <>
              <CheckCircledIcon className="size-5 text-green-500" />
              <span className="text-sm text-gray-600">
                Ready to export {memoQuery.data?.totalCount || 0} notes
              </span>
            </>
          ) : null}
        </div>
      )}

      {/* Results Table - shown after successful fetch */}
      {memoQuery.isSuccess && exportData.length > 0 && (
        <div className="rounded-lg border bg-white">
          <div className="max-h-80 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="w-24">Job #</TableHead>
                  <TableHead className="w-28">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exportData.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-xs">{item.id}</TableCell>
                    <TableCell className="max-w-32 truncate text-sm">
                      {truncateText(item.name, 30)}
                    </TableCell>
                    <TableCell className="max-w-40 truncate text-sm">
                      {truncateText(item.description, 40)}
                    </TableCell>
                    <TableCell className="text-sm">{item.createdByName}</TableCell>
                    <TableCell className="text-sm">{item.userDepartmentName}</TableCell>
                    <TableCell className="font-mono text-xs">{item.eventId || '-'}</TableCell>
                    <TableCell className="text-xs">
                      {item.createdDate ? item.createdDate.split(' ')[0] : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {exportData.length > 10 && (
            <div className="border-t px-4 py-2 text-xs text-gray-500">
              Showing {exportData.length} notes
            </div>
          )}
        </div>
      )}

      {/* No Results Message */}
      {memoQuery.isSuccess && exportData.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          No notes found matching the selected filters.
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button
          size="sm"
          variant="outline"
          onClick={handleSearchClick}
          disabled={memoQuery.isLoading}
        >
          {memoQuery.isLoading ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <MagnifyingGlassIcon className="mr-2 size-4" />
          )}
          Search
        </Button>
        {hasResultsReady && (
          <ExportJSON
            filename={getFilename}
            json={exportData}
            columns={exportColumns}
            onExported={handleExported}
          >
            Export to CSV
          </ExportJSON>
        )}
      </div>
    </div>
  );
};
